import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import KPICard from './components/KPICard';
import QuickActions from './components/QuickActions';
import EventsTable from './components/EventsTable';
import VenuesTable from './components/VenuesTable';
import MerchandiseTable from './components/MerchandiseTable';
import TeamManagement from './components/TeamManagement';
import RecentActivity from './components/RecentActivity';
import SalesChart from './components/SalesChart';

const EventManagerDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [scannerCode, setScannerCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Mock data for KPIs
  const kpiData = [
    {
      title: 'Total Sales',
      value: '$24,580',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'DollarSign',
      trend: true
    },
    {
      title: 'Active Events',
      value: '8',
      change: '+2',
      changeType: 'positive',
      icon: 'Calendar',
      trend: false
    },
    {
      title: 'Tickets Sold',
      value: '1,247',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'Ticket',
      trend: true
    },
    {
      title: 'Total Venues',
      value: '12',
      change: '+1',
      changeType: 'positive',
      icon: 'MapPin',
      trend: false
    }
  ];

  // Mock data for sales chart
  const salesData = [
    { date: '2025-09-22', sales: 2400 },
    { date: '2025-09-23', sales: 1398 },
    { date: '2025-09-24', sales: 9800 },
    { date: '2025-09-25', sales: 3908 },
    { date: '2025-09-26', sales: 4800 },
    { date: '2025-09-27', sales: 3800 },
    { date: '2025-09-28', sales: 4300 },
    { date: '2025-09-29', sales: 5200 }
  ];

  // Mock data for events
  const eventsData = [
    {
      id: 1,
      name: 'Summer Music Festival 2025',
      category: 'Music',
      date: '2025-07-15',
      venue: 'Central Park Amphitheater',
      status: 'published',
      totalSales: 45600,
      ticketsSold: 1240,
      totalTickets: 2000,
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400',
      createdAt: '2025-09-15'
    },
    {
      id: 2,
      name: 'Tech Conference 2025',
      category: 'Conference',
      date: '2025-10-20',
      venue: 'Convention Center',
      status: 'live',
      totalSales: 28900,
      ticketsSold: 578,
      totalTickets: 800,
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400',
      createdAt: '2025-09-10'
    },
    {
      id: 3,
      name: 'Art Gallery Opening',
      category: 'Art',
      date: '2025-11-05',
      venue: 'Modern Art Museum',
      status: 'draft',
      totalSales: 0,
      ticketsSold: 0,
      totalTickets: 150,
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400',
      createdAt: '2025-09-25'
    }
  ];

  // Mock data for venues
  const venuesData = [
    {
      id: 1,
      name: 'Central Park Amphitheater',
      type: 'Outdoor Venue',
      location: 'New York, NY',
      address: '123 Central Park West, New York, NY 10025',
      capacity: 2000,
      activeEvents: 3,
      status: 'active',
      image: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400',
      coordinates: { lat: 40.7829, lng: -73.9654 }
    },
    {
      id: 2,
      name: 'Convention Center',
      type: 'Conference Hall',
      location: 'Los Angeles, CA',
      address: '456 Convention Blvd, Los Angeles, CA 90015',
      capacity: 1500,
      activeEvents: 2,
      status: 'active',
      image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=400',
      coordinates: { lat: 34.0522, lng: -118.2437 }
    },
    {
      id: 3,
      name: 'Modern Art Museum',
      type: 'Museum',
      location: 'San Francisco, CA',
      address: '789 Museum Ave, San Francisco, CA 94102',
      capacity: 300,
      activeEvents: 1,
      status: 'active',
      image: 'https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=400',
      coordinates: { lat: 37.7749, lng: -122.4194 }
    }
  ];

  // Mock data for merchandise
  const merchandiseData = [
    {
      id: 1,
      name: 'Festival T-Shirt',
      sku: 'FEST-TSHIRT-001',
      category: 'apparel',
      price: 25,
      stock: 150,
      totalSales: 89,
      image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400'
    },
    {
      id: 2,
      name: 'Conference Mug',
      sku: 'CONF-MUG-002',
      category: 'accessories',
      price: 15,
      stock: 5,
      totalSales: 45,
      image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcf93a?w=400'
    },
    {
      id: 3,
      name: 'Art Print Collection',
      sku: 'ART-PRINT-003',
      category: 'collectibles',
      price: 50,
      stock: 0,
      totalSales: 12,
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400'
    }
  ];

  // Mock data for team members
  const teamMembersData = [
    {
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah@example.com',
      role: 'content-manager',
      status: 'active',
      lastActive: '2025-09-28',
      permissions: ['events', 'venues', 'merchandise'],
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400'
    },
    {
      id: 2,
      name: 'Mike Chen',
      email: 'mike@example.com',
      role: 'editor',
      status: 'active',
      lastActive: '2025-09-27',
      permissions: ['events', 'reports'],
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400'
    },
    {
      id: 3,
      name: 'Emily Davis',
      email: 'emily@example.com',
      role: 'content-manager',
      status: 'pending',
      lastActive: null,
      permissions: ['venues', 'merchandise'],
      avatar: null
    }
  ];

  // Mock data for recent activity
  const recentActivityData = [
    {
      id: 1,
      type: 'ticket_sale',
      title: 'New ticket sale',
      description: 'VIP ticket sold for Summer Music Festival 2025',
      timestamp: new Date(Date.now() - 300000),
      metadata: { amount: 150, event: 'Summer Music Festival 2025' }
    },
    {
      id: 2,
      type: 'event_created',
      title: 'Event created',
      description: 'Art Gallery Opening event was created',
      timestamp: new Date(Date.now() - 3600000),
      metadata: { user: 'Sarah Johnson', event: 'Art Gallery Opening' }
    },
    {
      id: 3,
      type: 'merchandise_sale',
      title: 'Merchandise sold',
      description: 'Festival T-Shirt purchased',
      timestamp: new Date(Date.now() - 7200000),
      metadata: { amount: 25, event: 'Summer Music Festival 2025' }
    },
    {
      id: 4,
      type: 'team_invite',
      title: 'Team member invited',
      description: 'Emily Davis was invited as Content Manager',
      timestamp: new Date(Date.now() - 86400000),
      metadata: { user: 'You' }
    },
    {
      id: 5,
      type: 'scanner_access',
      title: 'Scanner code generated',
      description: 'New 6-digit scanner code created for event access',
      timestamp: new Date(Date.now() - 172800000),
      metadata: {}
    }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const generateScannerCode = () => {
    const code = Math.floor(100000 + Math.random() * 900000)?.toString();
    setScannerCode(code);
  };

  const handleInviteMember = (inviteData) => {
    console.log('Inviting member:', inviteData);
    // Handle team member invitation
  };

  const handleUpdatePermissions = (memberId) => {
    console.log('Updating permissions for member:', memberId);
    // Handle permission updates
  };

  const handleStockUpdate = (itemId) => {
    console.log('Updating stock for item:', itemId);
    // Handle stock updates
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'events', label: 'Events', icon: 'Calendar' },
    { id: 'venues', label: 'Venues', icon: 'MapPin' },
    { id: 'merchandise', label: 'Merchandise', icon: 'Package' },
    { id: 'team', label: 'Team', icon: 'Users' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-lg font-medium text-foreground">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Ticket" size={20} color="white" />
                </div>
                <span className="text-xl font-semibold text-foreground">Tixful</span>
              </div>
              <div className="hidden md:block">
                <span className="text-sm text-muted-foreground">Event Manager Dashboard</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                iconName="QrCode"
                onClick={() => navigate('/event-scanner')}
              >
                Scanner
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Bell"
                onClick={() => {}}
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="User"
                onClick={() => navigate('/my-account')}
              />
            </div>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Welcome back, Event Manager
          </h1>
          <p className="text-muted-foreground">
            Here's what's happening with your events today, {new Date()?.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-border">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {kpiData?.map((kpi, index) => (
                <KPICard key={index} {...kpi} />
              ))}
            </div>

            {/* Quick Actions */}
            <QuickActions
              scannerCode={scannerCode}
              onGenerateCode={generateScannerCode}
            />

            {/* Charts and Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <SalesChart data={salesData} />
              <RecentActivity activities={recentActivityData} />
            </div>
          </div>
        )}

        {activeTab === 'events' && (
          <EventsTable
            events={eventsData}
            onStatusChange={(eventId, status) => console.log('Status change:', eventId, status)}
          />
        )}

        {activeTab === 'venues' && (
          <VenuesTable
            venues={venuesData}
            onVenueAction={(venueId, action) => console.log('Venue action:', venueId, action)}
          />
        )}

        {activeTab === 'merchandise' && (
          <MerchandiseTable
            merchandise={merchandiseData}
            onStockUpdate={handleStockUpdate}
          />
        )}

        {activeTab === 'team' && (
          <TeamManagement
            teamMembers={teamMembersData}
            onInviteMember={handleInviteMember}
            onUpdatePermissions={handleUpdatePermissions}
          />
        )}
      </div>
    </div>
  );
};

export default EventManagerDashboard;